import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Play, ArrowRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { extractYouTubeVideoId } from "@/lib/youtube";
import { SponsoredVideoModal } from "@/components/SponsoredVideoModal";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Button } from "@/components/ui/button";

interface StoryCard {
  id: string;
  title: string;
  title_above: string | null;
  sponsor_text: string | null;
  videoId: string;
  thumb: string;
}

export const StoriesShowcase = () => {
  const [stories, setStories] = useState<StoryCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState<StoryCard | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("sponsored_ads")
        .select("id, title, title_above, sponsor_text, thumbnail_url, youtube_video_id")
        .eq("is_public", true)
        .order("created_at", { ascending: false })
        .limit(6);
      if (cancelled) return;
      if (error) {
        console.error("StoriesShowcase fetch error:", error);
        setStories([]);
      } else {
        setStories(
          (data ?? [])
            .map((ad) => {
              const videoId = extractYouTubeVideoId(ad.youtube_video_id ?? "");
              if (!videoId) return null;
              return {
                id: ad.id,
                title: ad.title,
                title_above: ad.title_above,
                sponsor_text: ad.sponsor_text,
                videoId,
                thumb:
                  ad.thumbnail_url ||
                  `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`,
              } as StoryCard;
            })
            .filter((x): x is StoryCard => x !== null)
        );
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading || stories.length === 0) return null;

  return (
    <section className="bg-background border-t border-border/40 py-12 sm:py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
          <div>
            <p className="text-xs font-semibold text-primary uppercase tracking-wider mb-1">
              Local spotlights
            </p>
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground leading-tight">
              Meet the places and people we love
            </h2>
            <p className="text-muted-foreground mt-2 max-w-xl">
              Short video stories from local businesses and venues around Balham.
            </p>
          </div>
          <Button asChild variant="outline" className="self-start sm:self-auto shrink-0">
            <Link to="/local-spotlights">
              View all spotlights
              <ArrowRight className="ml-1 h-4 w-4" />
            </Link>
          </Button>
        </div>

        <div className="flex gap-4 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 snap-x snap-mandatory">
          {stories.map((story) => (
            <button
              key={story.id}
              onClick={() => setActive(story)}
              className="group snap-start shrink-0 w-[280px] sm:w-[320px] text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-xl"
              aria-label={`Play story: ${story.title}`}
            >
              <div className="relative rounded-xl overflow-hidden shadow-soft">
                <AspectRatio ratio={16 / 9}>
                  <img
                    src={story.thumb}
                    alt={story.title}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground/70 via-transparent to-transparent" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-12 h-12 rounded-full bg-background/90 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                      <Play className="w-5 h-5 text-primary ml-0.5" fill="currentColor" />
                    </div>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-3">
                    {story.title_above && (
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-primary-foreground/80 mb-0.5">
                        {story.title_above}
                      </p>
                    )}
                    <h3 className="text-sm font-semibold text-primary-foreground leading-snug line-clamp-2">
                      {story.title}
                    </h3>
                  </div>
                </AspectRatio>
              </div>
              {story.sponsor_text && (
                <p className="text-xs text-muted-foreground mt-2 line-clamp-1">
                  {story.sponsor_text}
                </p>
              )}
            </button>
          ))}
        </div>
      </div>

      {active && (
        <SponsoredVideoModal
          open={!!active}
          onOpenChange={(o) => !o && setActive(null)}
          videoId={active.videoId}
          title={active.title}
          adId={active.id}
        />
      )}
    </section>
  );
};
